import { useEffect, useState } from "react";
import { X, Globe2 } from "lucide-react";
import { toast } from "react-toastify";
import { db, collection, addDoc, serverTimestamp } from "../firebase";
import { detectVisitorTimezone, isLikelyEuropeanTimezone } from "../utils/regionGate";

const DISMISS_KEY = "regionNoticeDismissed";

/**
 * Soft, dismissible notice shown only to visitors who are likely outside
 * Europe. Never blocks the page — it only sets accurate expectations about
 * current coverage (Mon–Sat, 07:00–23:00 CET) and offers a waitlist signup
 * for when the platform expands to other continents.
 */
function RegionNotice() {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") return;
    } catch (err) {
      // localStorage can throw in some privacy modes — fail open (show once)
    }
    const timezone = detectVisitorTimezone();
    if (isLikelyEuropeanTimezone(timezone) === false) {
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch (err) {
      // ignore
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      toast.error("Enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, "waitlist"), {
        email: trimmed,
        detectedTimezone: detectVisitorTimezone(),
        source: "region_notice",
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
      toast.success("You're on the list — we'll email you when we expand.");
    } catch (err) {
      console.warn("Could not save waitlist signup", err);
      toast.error("Could not save your email. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="bg-amber-50 border-b border-amber-200 text-amber-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row items-center gap-3">
        <div className="flex items-start sm:items-center gap-2 flex-1">
          <Globe2 className="w-4 h-4 mt-0.5 sm:mt-0 flex-shrink-0" />
          <p className="text-xs sm:text-sm">
            Our 2-hour expert guarantee currently covers{" "}
            <span className="font-semibold">Europe, Mon–Sat 07:00–23:00 CET</span>.
            Outside that region? Response times may be longer.
          </p>
        </div>

        {submitted ? (
          <span className="text-xs sm:text-sm font-medium whitespace-nowrap">
            ✓ You're on the waitlist
          </span>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex items-center gap-2 w-full sm:w-auto"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              className="min-w-0 flex-1 sm:w-48 text-xs sm:text-sm px-3 py-1.5 rounded-lg border border-amber-300 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
            <button
              type="submit"
              disabled={submitting}
              className="whitespace-nowrap text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-lg bg-amber-900 text-white hover:bg-amber-800 transition disabled:opacity-50"
            >
              {submitting ? "Saving..." : "Notify me"}
            </button>
          </form>
        )}

        <button
          onClick={dismiss}
          aria-label="Dismiss notice"
          className="flex-shrink-0 p-1 rounded-full hover:bg-amber-100 transition"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default RegionNotice;
